import { useEffect, useRef, useState } from 'react'
import { api, setToken } from '../api'
import { telegramWebApp } from '../telegram'

/**
 * Экран входа через Telegram Login Widget.
 *
 * Виджет — сторонний скрипт telegram.org, который сам рисует кнопку в
 * контейнере. Он требует HTTPS и домен, зарегистрированный у BotFather
 * (`/setdomain`), поэтому на localhost не работает — для разработки есть
 * анонимный режим на сервере.
 */
export function Login({
  botUsername,
  onDone,
}: {
  botUsername: string
  onDone: () => void
}) {
  const holder = useRef<HTMLDivElement>(null)
  const [error, setError] = useState<string | null>(null)
  const insideTelegram = telegramWebApp() !== null

  useEffect(() => {
    if (insideTelegram || !botUsername || !holder.current) return

    // Виджет зовёт глобальную функцию по имени из data-onauth.
    ;(window as unknown as Record<string, unknown>).onTelegramAuth = (
      user: Record<string, unknown>,
    ) => {
      api
        .authTelegram(user)
        .then((r) => {
          setToken(r.token)
          onDone()
        })
        .catch((e) => setError(e instanceof Error ? e.message : String(e)))
    }

    const script = document.createElement('script')
    script.src = 'https://telegram.org/js/telegram-widget.js?22'
    script.async = true
    script.setAttribute('data-telegram-login', botUsername)
    script.setAttribute('data-size', 'large')
    script.setAttribute('data-radius', '12')
    script.setAttribute('data-onauth', 'onTelegramAuth(user)')
    script.setAttribute('data-request-access', 'write')
    holder.current.appendChild(script)

    const node = holder.current
    return () => {
      node.innerHTML = ''
    }
  }, [botUsername, insideTelegram, onDone])

  return (
    <div className="flex h-full items-center justify-center px-4">
      <div className="w-full max-w-sm text-center">
        <h1 className="mb-1 text-2xl font-semibold">Озарник</h1>
        <p className="mb-6 text-sm text-(--ink-dim)">
          войди через телеграм — переписка будет общей с ботом
        </p>

        {insideTelegram ? (
          // Виджет внутри Telegram не отрисуется никогда, поэтому и не
          // пытаемся: если мы здесь, значит вход по initData не прошёл.
          <div className="rounded-xl border border-(--hairline) bg-(--surface) px-4 py-3 text-left text-sm text-(--ink)">
            <p className="font-medium">не вышло войти из телеграма</p>
            <p className="mt-1 text-xs text-(--ink-dim)">
              открой приложение заново через кнопку у бота. если не поможет —
              проверь, что адрес мини-приложения у @BotFather совпадает с этим.
            </p>
          </div>
        ) : botUsername ? (
          <div ref={holder} className="flex justify-center" />
        ) : (
          <div className="rounded-xl border border-(--hairline) bg-(--surface) px-4 py-3 text-left text-sm text-(--ink)">
            <p className="font-medium">вход не настроен</p>
            <p className="mt-1 text-xs text-(--ink-dim)">
              задайте <code className="font-mono">OZARNIK_TG_BOT_USERNAME</code> в{' '}
              <code className="font-mono">.env</code>, либо{' '}
              <code className="font-mono">OZARNIK_WEB_ALLOW_ANONYMOUS=true</code> для
              локальной разработки
            </p>
          </div>
        )}

        {error && (
          <p className="mt-4 border-l-2 border-(--ink-faint) pl-3 text-left text-sm text-(--ink-dim)">
            {error}
          </p>
        )}
      </div>
    </div>
  )
}
